import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  View,
  ScrollView,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Modal,
  Dimensions,
  Animated,
  Platform,
  TextInput,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { buttonsMenu, ButtonList } from "../components/Common/ComponentButtons.js";
import { useSelector } from "react-redux";
import { SCREEN_IDS } from "../components/FullComponent.js";
import { typeConnect } from "../constants/Constants";
import {
  versionJTS,
  backgroundColor,
} from "../constants/Constants";
import { getFullMoForStone } from "../services/ConnectDataBase.js";
import { getReferenciasSinImagenes as getReferenciasSinImagenesAPI } from "../services/api/operations/articles/ArticlesApi.js";
import { MaterialCommunityIcons, Ionicons } from "@expo/vector-icons";
import LanguageToggleButton from "../components/Common/ComponentLanguageToggleButton.js";
import ComponentProcessAlertsModal from "../components/Common/ComponentProcessAlertsModal.js";
import * as XLSX from "xlsx";
import {
  ADMIN_SETTING_SCREEN_COLORS,
  BACKGROUND_COLORS,
  BORDER_COLORS,
  BRAND_COLORS,
  SHADOW_COLORS,
  STATUS_COLORS,
  TEXT_COLORS,
} from "../style/colors.js";
import { getErrorMessage, notify } from "../services/ui/notify";

const { width: screenWidth, height: screenHeight } = Dimensions.get("window");
// Función para detectar si es versión de prueba
const isTestVersion = () => {
  if (typeof window !== 'undefined' && window.location) {
    console.log(window.location.href);
    return window.location.href.includes('JTS_TEST');
  }
  return false;
};

// Configuración de alertas
const ALERT_CONFIG = {
  DAYS_THRESHOLD: 3,
  HIGH_PRIORITY_DAYS: 7,
  TEAMS_WITH_ALERTS: [
    "Dirección",
    "Jefe de taller", 
    "IT",
    "Director técnico de",
    "Dpto.Piedras",
  ],
  ANIMATION_CONFIG: {
    PULSE_DURATION: 2000,
    PULSE_SCALE: 1.05,
    FADE_DURATION: 300,
    SCALE_DURATION: 200,
  }
};

// Función para calcular días de diferencia
const calculateDaysDifference = (date) => {
  const today = new Date();
  return Math.ceil((today - date) / (1000 * 60 * 60 * 24));
};

// Función para crear mensaje de alerta
const createAlertMessage = (mo, type, date, days) => {
  const formattedDate = date.split(" ")[0];
  
  switch (type) {
    case "entrada_sin_bagging":
      return `${mo} - Entrada/Piedras: ${formattedDate} sin Bagging - días: ${days}`;
    case "bagging_sin_salida":
      return `${mo} - Bagging Completo: ${formattedDate} sin Salida PIEDRAS.`;
    case "bagging_anterior_entrada":
      return `${mo} - Bagging Completo: ${mo}. Entrada en PIEDRAS: ${formattedDate} sin 'Salida PIEDRAS.`;
    default:
      return `${mo} - Alerta pendiente`;
  }
};


// Función para validar estado de piedras (simplificada)
const validateStoneStatus = (data, formatDate) => {
  const today = new Date();
  const newMessages = [];

  data.forEach((item) => {
    const {
      mo,
      existe_entrada_piedras,
      existe_bagging_completo,
      existe_salida_piedras_ent,
      fecha_entrada_piedras,
      fecha_bagging_completo,
      fecha_salida_piedras_ent
    } = item;

    const entradaPiedras = existe_entrada_piedras === "true";
    const baggingCompleto = existe_bagging_completo === "true";
    const salidaPiedras = existe_salida_piedras_ent === "true";

    const fechaEntradaPiedras = fecha_entrada_piedras?.date 
      ? new Date(fecha_entrada_piedras.date) 
      : null;
    const fechaBaggingCompleto = fecha_bagging_completo?.date 
      ? new Date(fecha_bagging_completo.date) 
      : null;
    const fechaSalidaPiedras = fecha_salida_piedras_ent?.date 
      ? new Date(fecha_salida_piedras_ent.date) 
      : null;

    // Caso 1: Entrada sin bagging
    if (entradaPiedras && !baggingCompleto && fechaEntradaPiedras) {
      const diffDays = calculateDaysDifference(fechaEntradaPiedras);
      if (diffDays > ALERT_CONFIG.DAYS_THRESHOLD) {
        newMessages.push({
          mo,
          message: createAlertMessage(mo, "entrada_sin_bagging", fecha_entrada_piedras.date, diffDays),
          priority: diffDays > ALERT_CONFIG.HIGH_PRIORITY_DAYS ? "high" : "medium",
          days: diffDays,
        });
      }
    }

    // Caso 2: Bagging completo sin salida
    if (baggingCompleto && fechaBaggingCompleto && !fechaSalidaPiedras) {
      const diffDays = calculateDaysDifference(fechaBaggingCompleto);
      if (diffDays > ALERT_CONFIG.DAYS_THRESHOLD && (entradaPiedras || salidaPiedras)) {
        newMessages.push({
          mo,
          message: createAlertMessage(mo, "bagging_sin_salida", fecha_bagging_completo.date, diffDays),
          priority: diffDays > ALERT_CONFIG.HIGH_PRIORITY_DAYS ? "high" : "medium",
          days: diffDays,
        });
      }
    }

    // Caso 3: Bagging anterior a entrada sin salida
    if (fechaEntradaPiedras && fechaBaggingCompleto < fechaEntradaPiedras && !fechaSalidaPiedras) {
      const diffDays = calculateDaysDifference(fechaEntradaPiedras);
      if (diffDays > ALERT_CONFIG.DAYS_THRESHOLD) {
        newMessages.push({
          mo,
          message: createAlertMessage(mo, "bagging_anterior_entrada", fecha_entrada_piedras.date, diffDays),
          priority: diffDays > ALERT_CONFIG.HIGH_PRIORITY_DAYS ? "high" : "medium",
          days: diffDays,
        });
      }
    }
  });

  // Eliminar duplicados y ordenar
  const uniqueMessages = newMessages.filter(
    (message, index, self) => index === self.findIndex((m) => m.mo === message.mo)
  );

  uniqueMessages.sort((a, b) => {
    if (a.priority === "high" && b.priority !== "high") return -1;
    if (b.priority === "high" && a.priority !== "high") return 1;
    return (b.days || 0) - (a.days || 0);
  });

  return uniqueMessages;
};

export default function AdminSettingScreen({ navigation }) {
  const equipo = useSelector((state) => state.user.team);
  const usuario = useSelector((state) => state.user.name);
  const nivelAcceso = useSelector((state) => state.user.nivel_acceso);
  const [messages, setMessages] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Estados para referencias sin imágenes
  const [referenciasSinImagen, setReferenciasSinImagen] = useState([]);
  const [modalReferenciasVisible, setModalReferenciasVisible] = useState(false);
  const [isLoadingReferencias, setIsLoadingReferencias] = useState(false);
  const [isExpandedReferencias, setIsExpandedReferencias] = useState(false);
  const [referenciasLoaded, setReferenciasLoaded] = useState(false); // Para saber si ya se cargaron los datos
  const [searchTermReferencias, setSearchTermReferencias] = useState("");
  const [searchTermPiedras, setSearchTermPiedras] = useState("");
  const [modalProcessAlertsVisible, setModalProcessAlertsVisible] = useState(false);

  // Animaciones
  const fadeAnim = useState(new Animated.Value(0))[0];
  const scaleAnim = useState(new Animated.Value(0.8))[0];
  const fadeAnimReferencias = useState(new Animated.Value(0))[0];
  const scaleAnimReferencias = useState(new Animated.Value(0.8))[0];

  const shouldShowAlert = useMemo(
    () => ALERT_CONFIG.TEAMS_WITH_ALERTS.includes(equipo),
    [equipo]
  );

  const headerConfig = useMemo(
    () => ({
      headerRight: () => (
        <View style={styles.headerRightContainer}>
          <View style={styles.headerUserInfo}>
            <View style={styles.headerUserBadge}>
              <MaterialCommunityIcons
                name="account"
                size={16}
                color={TEXT_COLORS.LIGHT}
              />
              <Text style={styles.headerUserText}>{usuario}</Text>
            </View>
            <View style={styles.headerVersionBadge}>
              <MaterialCommunityIcons
                name="diamond-stone"
                size={16}
                color={TEXT_COLORS.LIGHT}
              />
              <Text style={styles.headerVersionText}>{versionJTS}</Text>
            </View>
          </View>
          <LanguageToggleButton />
          {equipo === "IT" && (
            <TouchableOpacity
              onPress={() => setSettingsModalVisible(true)}
              style={styles.headerButton}>
              <Ionicons name='settings-outline' size={25} color='white' />
            </TouchableOpacity>
          )}
        </View>
      ),
      headerLeft: () => (
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.headerButton}>
          <Ionicons name='power-outline' size={25} color='white' />
        </TouchableOpacity>
      ),
    }),
    [equipo, navigation, usuario, versionJTS]
  );

  useEffect(() => {
    navigation.setOptions(headerConfig);
  }, [navigation, headerConfig]);

  const formatDate = useCallback((dateString) => {
    return dateString.split(" ")[0];
  }, []);

  const validateStatusStone = useCallback(
    (data) => {
      const validatedMessages = validateStoneStatus(data, formatDate);
      setMessages(validatedMessages);
    },
    [formatDate]
  );

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await getFullMoForStone(
        "StonceControlScreen",
        typeConnect[0],
        SCREEN_IDS.STONE_CONTROL
      );
      validateStatusStone(result.data);
      setModalVisible(false);
    } catch (error) {
      console.error("Error al obtener datos de piedras:", error);
      notify.error(
        getErrorMessage(
          error,
          "No se pudieron cargar los datos de control de piedras. Inténtalo nuevamente."
        )
      );
    } finally {
      setIsLoading(false);
    }
  }, [validateStatusStone]);

  // Función para obtener referencias sin imágenes
  const fetchReferenciasSinImagen = useCallback(async () => {
    try {
      setIsLoadingReferencias(true);
      const response = await getReferenciasSinImagenesAPI();

      // La API puede devolver un array directamente o dentro de un objeto
      if (Array.isArray(response)) {
        setReferenciasSinImagen(response);
      } else if (response && Array.isArray(response.data)) {
        setReferenciasSinImagen(response.data);
      } else if (response && typeof response === 'object') {
        // Si viene como objeto con referencias como propiedades
        const referenciasArray = Object.entries(response).map(([ref, data]) => ({
          referencia: ref,
          descripcion: data.descripcion || "",
          fullRefs: data.FullRef || [],
        }));
        setReferenciasSinImagen(referenciasArray);
      } else {
        setReferenciasSinImagen([]);
      }
      
      setReferenciasLoaded(true);
    } catch (error) {
      console.error("Error al obtener referencias sin imágenes:", error);
      setReferenciasLoaded(true); // Marcar como cargado incluso si hay error
      notify.error(
        getErrorMessage(
          error,
          "No se pudieron cargar las referencias sin imágenes. Inténtalo nuevamente."
        )
      );
    } finally {
      setIsLoadingReferencias(false);
    }
  }, []);

  // Ejecutar ambas llamadas al mismo tiempo al montar el componente
  useEffect(() => {
    if (shouldShowAlert) {
      // Ejecutar ambas llamadas en paralelo
      fetchData();
      fetchReferenciasSinImagen();
    }
  }, [shouldShowAlert, fetchData, fetchReferenciasSinImagen]);

  const handleExportExcel = useCallback(() => {
    if (messages.length === 0) {
      notify.info("No hay datos para exportar.");
      return;
    }

    try {
      const formattedData = messages.map((row) => ({
        mo: row.mo || "",
        message: row.message || "",
        prioridad: row.priority || "",
        dias: row.days || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(formattedData, {
        cellDates: true,
        dateNF: "dd/mm/yyyy",
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Validaciones Piedras");

      XLSX.writeFile(workbook, "validaciones_piedras.xlsx");
      notify.success("Se exportaron las validaciones de piedras.");
    } catch (error) {
      console.error("Error al exportar Excel:", error);
      notify.error(getErrorMessage(error, "No se pudo exportar el archivo Excel."));
    }
  }, [messages]);

  const showModal = useCallback(() => {
    setModalVisible(true);
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: ALERT_CONFIG.ANIMATION_CONFIG.FADE_DURATION,
        useNativeDriver: true,
      }),
      Animated.spring(scaleAnim, {
        toValue: 1,
        friction: 8,
        tension: 100,
        useNativeDriver: true,
      }),
    ]).start();
  }, [fadeAnim, scaleAnim]);

  const hideSettingsModal = useCallback(() => {
    setSettingsModalVisible(false);
  }, []);

  const showModalReferencias = useCallback(() => {
    setModalReferenciasVisible(true);
    Animated.parallel([
      Animated.timing(fadeAnimReferencias, {
        toValue: 1,
        duration: ALERT_CONFIG.ANIMATION_CONFIG.FADE_DURATION,
        useNativeDriver: true,
      }),
      Animated.spring(scaleAnimReferencias, {
        toValue: 1,
        friction: 8,
        tension: 100,
        useNativeDriver: true,
      }),
    ]).start();
  }, [fadeAnimReferencias, scaleAnimReferencias]);

  const hideModalReferencias = useCallback(() => {
    Animated.parallel([
      Animated.timing(fadeAnimReferencias, {
        toValue: 0,
        duration: ALERT_CONFIG.ANIMATION_CONFIG.SCALE_DURATION,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnimReferencias, {
        toValue: 0.8,
        duration: ALERT_CONFIG.ANIMATION_CONFIG.SCALE_DURATION,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setModalReferenciasVisible(false);
      setIsExpandedReferencias(false);
      setSearchTermReferencias(""); // Limpiar búsqueda al cerrar
    });
  }, [fadeAnimReferencias, scaleAnimReferencias]);

  const hideModal = useCallback(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: ALERT_CONFIG.ANIMATION_CONFIG.SCALE_DURATION,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 0.8,
        duration: ALERT_CONFIG.ANIMATION_CONFIG.SCALE_DURATION,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setModalVisible(false);
      setIsExpanded(false);
      setSearchTermPiedras(""); // Limpiar búsqueda al cerrar
    });
  }, [fadeAnim, scaleAnim]);

  const toggleExpandedReferencias = useCallback(() => {
    setIsExpandedReferencias(!isExpandedReferencias);
  }, [isExpandedReferencias]);

  const handleExportExcelReferencias = useCallback(() => {
    if (referenciasSinImagen.length === 0) {
      notify.info("No hay datos para exportar.");
      return;
    }

    try {
      const formattedData = referenciasSinImagen.map((row) => {
        // Manejar fullRefs que puede ser array o no existir
        let fullRefsStr = "";
        if (row.fullRefs && Array.isArray(row.fullRefs) && row.fullRefs.length > 0) {
          fullRefsStr = row.fullRefs.join(", ");
        }
        
        return {
          referencia: row.referencia || "",
          descripcion: row.descripcion || "",
          fullRefs: fullRefsStr,
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(formattedData, {
        cellDates: true,
        dateNF: "dd/mm/yyyy",
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Referencias Sin Imagen");

      XLSX.writeFile(workbook, "referencias_sin_imagen.xlsx");
      notify.success("Se exportaron las referencias sin imagen.");
    } catch (error) {
      console.error("Error al exportar Excel:", error);
      notify.error(getErrorMessage(error, "No se pudo exportar el archivo Excel."));
    }
  }, [referenciasSinImagen]);

  const navigateToScreen = useCallback(
    (screenName) => {
      hideSettingsModal();
      navigation.navigate(screenName);
    },
    [navigation, hideSettingsModal]
  );

  const toggleExpanded = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const AlertItem = React.memo(({ item }) => (
    <View
      style={[
        styles.alertItem,
        item.priority === "high" && styles.alertItemHigh,
      ]}>
      <View style={styles.alertItemHeader}>
        <View
                      style={[
              styles.priorityIndicator,
              {
                backgroundColor: item.priority === "high" ? ADMIN_SETTING_SCREEN_COLORS.PRIORITY_HIGH : ADMIN_SETTING_SCREEN_COLORS.PRIORITY_MEDIUM,
              },
            ]}
        />
        <Text style={styles.alertMoText}>{item.mo}</Text>
        {item.days && (
          <View style={styles.daysChip}>
            <Text style={styles.daysText}>{item.days}d</Text>
          </View>
        )}
      </View>
      <Text style={styles.alertText}>{item.message}</Text>
    </View>
  ));

  const ReferenciaItem = React.memo(({ item }) => (
    <View style={styles.alertItem}>
      <View style={styles.alertItemHeader}>
        <MaterialCommunityIcons
          name='image-off'
          size={16}
          color={ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY}
          style={{ marginRight: 8 }}
        />
        <Text style={styles.alertMoText}>{item.referencia}</Text>
      </View>
      {item.descripcion && (
        <Text style={styles.alertText}>{item.descripcion}</Text>
      )}
      {item.fullRefs && item.fullRefs.length > 0 && (
        <Text style={[styles.alertText, { marginTop: 4, fontSize: 11, color: ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY }]}>
          FullRefs: {item.fullRefs.join(", ")}
        </Text>
      )}
    </View>
  ));

  // Función para filtrar referencias sin imágenes
  const filteredReferenciasSinImagen = useMemo(() => {
    if (!searchTermReferencias.trim()) {
      return referenciasSinImagen;
    }
    const searchLower = searchTermReferencias.toLowerCase();
    return referenciasSinImagen.filter((item) => {
      const referencia = (item.referencia || "").toLowerCase();
      const descripcion = (item.descripcion || "").toLowerCase();
      const fullRefs = (item.fullRefs || []).join(" ").toLowerCase();
      return referencia.includes(searchLower) || 
             descripcion.includes(searchLower) || 
             fullRefs.includes(searchLower);
    });
  }, [referenciasSinImagen, searchTermReferencias]);

  // Función para filtrar alertas de piedras
  const filteredMessages = useMemo(() => {
    if (!searchTermPiedras.trim()) {
      return messages;
    }
    const searchLower = searchTermPiedras.toLowerCase();
    return messages.filter((item) => {
      const mo = (item.mo || "").toLowerCase();
      const message = (item.message || "").toLowerCase();
      const priority = (item.priority || "").toLowerCase();
      const days = String(item.days || "").toLowerCase();
      return mo.includes(searchLower) || 
             message.includes(searchLower) || 
             priority.includes(searchLower) || 
             days.includes(searchLower);
    });
  }, [messages, searchTermPiedras]);

  const settingsOptions = useMemo(
    () => [
      { title: "Usuarios", screen: "Usuarios", icon: "account-group" },
      {
        title: "Control de datos",
        screen: "Control de datos",
        icon: "database-cog",
      },
    ],
    []
  );

  return (
    <LinearGradient colors={backgroundColor()} style={styles.background}>
      {/* SVG Background Animation */}
      <View style={styles.svgBackgroundContainer}>
        <svg
          version="1.1"
          id="home-anim"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          x="0px"
          y="0px"
          viewBox="0 0 1820 1080"
          style={styles.svgBackground}
          className="animated-svg">
          <g id="home">
            <defs>
              <rect id="masque" y="0.4" width="1820" height="1080" />
            </defs>
            <clipPath id="cache">
              <use xlinkHref="#masque" style={{ overflow: "visible" }} />
            </clipPath>
            <g id="light-blue">
              <line x1="630.8" y1="894.3" x2="476.3" y2="1048.8" />
              <line x1="858.2" y1="823.9" x2="1012.7" y2="669.4" />
              <line x1="1066.9" y1="458.2" x2="912.4" y2="612.7" />
              <line x1="1294.3" y1="387.8" x2="1448.8" y2="233.3" />
              <line x1="1503" y1="22.1" x2="1348.5" y2="176.6" />
              <line x1="895.6" y1="1166.6" x2="1050.1" y2="1012.1" />
              <line x1="1104.3" y1="800.9" x2="949.8" y2="955.4" />
              <line x1="1331.7" y1="730.5" x2="1486.2" y2="576" />
              <line x1="1540.4" y1="364.8" x2="1385.9" y2="519.3" />
              <line x1="1767.8" y1="294.4" x2="1922.3" y2="139.9" />
              <line x1="1976.5" y1="-71.3" x2="1822" y2="83.2" />
              <line x1="1369.1" y1="1073.2" x2="1523.6" y2="918.7" />
              <line x1="1577.8" y1="707.5" x2="1423.3" y2="862" />
              <line x1="1805.2" y1="637.1" x2="1959.7" y2="482.6" />
              <line x1="1624" y1="1041.4" x2="1469.4" y2="1195.9" />
              <line x1="-134.7" y1="674.9" x2="19.8" y2="520.4" />
              <line x1="74" y1="309.2" x2="-80.5" y2="463.7" />
              <line x1="301.4" y1="238.8" x2="455.9" y2="84.3" />
              <line x1="510.1" y1="-126.9" x2="355.6" y2="27.6" />
              <line x1="-88.6" y1="1008.9" x2="65.9" y2="854.4" />
              <line x1="120.1" y1="643.1" x2="-34.4" y2="797.7" />
              <line x1="347.5" y1="572.8" x2="502" y2="418.3" />
              <line x1="556.2" y1="207.1" x2="401.7" y2="361.6" />
              <line x1="783.6" y1="136.7" x2="938.1" y2="-17.8" />
              <line x1="157.6" y1="985.8" x2="3" y2="1140.3" />
              <line x1="384.9" y1="915.5" x2="539.4" y2="760.9" />
              <line x1="593.6" y1="549.7" x2="439.1" y2="704.3" />
              <line x1="821" y1="479.4" x2="975.5" y2="324.9" />
              <line x1="1029.7" y1="113.6" x2="875.2" y2="268.2" />
              <line x1="1257.1" y1="43.3" x2="1411.6" y2="-111.2" />
            </g>
            <g id="red">
              <line x1="794.4" y1="883.4" x2="639.8" y2="1037.9" />
              <line x1="694.6" y1="834.8" x2="849.2" y2="680.3" />
              <line x1="1230.4" y1="447.3" x2="1075.9" y2="601.8" />
              <line x1="1130.7" y1="398.7" x2="1285.2" y2="244.2" />
              <line x1="1666.5" y1="11.2" x2="1512" y2="165.7" />
              <line x1="732" y1="1177.5" x2="886.6" y2="1023" />
              <line x1="1267.9" y1="790" x2="1113.3" y2="944.5" />
              <line x1="1168.1" y1="741.4" x2="1322.7" y2="586.9" />
              <line x1="1703.9" y1="353.9" x2="1549.4" y2="508.4" />
              <line x1="1604.2" y1="305.3" x2="1758.7" y2="150.8" />
              <line x1="1205.5" y1="1084.1" x2="1360.1" y2="929.6" />
              <line x1="1741.4" y1="696.5" x2="1586.8" y2="851.1" />
              <line x1="1641.6" y1="648" x2="1796.2" y2="493.5" />
              <line x1="1787.5" y1="1030.5" x2="1633" y2="1185" />
              <line x1="1687.8" y1="981.9" x2="1842.3" y2="827.4" />
              <line x1="200.1" y1="-44.4" x2="45.6" y2="110.1" />
              <line x1="237.5" y1="298.3" x2="83" y2="452.8" />
              <line x1="137.8" y1="249.7" x2="292.3" y2="95.2" />
              <line x1="673.6" y1="-137.8" x2="519.1" y2="16.7" />
              <line x1="283.7" y1="632.2" x2="129.2" y2="786.8" />
              <line x1="184" y1="583.7" x2="338.5" y2="429.2" />
              <line x1="719.8" y1="196.2" x2="565.2" y2="350.7" />
              <line x1="620" y1="147.6" x2="774.6" y2="-6.9" />
              <line x1="321.1" y1="974.9" x2="166.6" y2="1129.4" />
              <line x1="221.4" y1="926.4" x2="375.9" y2="771.8" />
              <line x1="757.2" y1="538.8" x2="602.7" y2="693.4" />
              <line x1="657.5" y1="490.3" x2="812" y2="335.8" />
              <line x1="1193.3" y1="102.7" x2="1038.7" y2="257.3" />
              <line x1="1093.5" y1="54.2" x2="1248.1" y2="-100.3" />
            </g>
            <g id="blue">
              <line x1="225.8" y1="1151" x2="534.9" y2="841.9" />
              <line x1="827.1" y1="1003.3" x2="518" y2="1312.3" />
              <line x1="661.9" y1="714.9" x2="971" y2="405.9" />
              <line x1="1263.1" y1="567.2" x2="954.1" y2="876.3" />
              <line x1="1098" y1="278.8" x2="1407.1" y2="-30.2" />
              <line x1="1699.2" y1="131.1" x2="1390.2" y2="440.2" />
              <line x1="699.3" y1="1057.6" x2="1008.4" y2="748.5" />
              <line x1="1300.6" y1="909.9" x2="991.5" y2="1218.9" />
              <line x1="1135.4" y1="621.5" x2="1444.5" y2="312.4" />
              <line x1="1736.6" y1="473.8" x2="1427.6" y2="782.8" />
              <line x1="1571.5" y1="185.4" x2="1880.6" y2="-123.6" />
              <line x1="1172.8" y1="964.2" x2="1481.9" y2="655.1" />
              <line x1="1774.1" y1="816.5" x2="1465" y2="1125.5" />
              <line x1="1608.9" y1="528.1" x2="1918" y2="219" />
              <line x1="1219" y1="1298.1" x2="1528" y2="989.1" />
              <line x1="1655.1" y1="862" x2="1964.1" y2="553" />
              <line x1="232.8" y1="75.5" x2="-76.2" y2="384.6" />
              <line x1="270.2" y1="418.2" x2="-38.8" y2="727.3" />
              <line x1="105.1" y1="129.8" x2="414.2" y2="-179.2" />
              <line x1="706.3" y1="-17.9" x2="397.3" y2="291.2" />
              <line x1="-284.8" y1="899.9" x2="24.2" y2="590.8" />
              <line x1="316.4" y1="752.2" x2="7.3" y2="1061.2" />
              <line x1="151.3" y1="463.8" x2="460.3" y2="154.7" />
              <line x1="752.5" y1="316.1" x2="443.4" y2="625.1" />
              <line x1="587.3" y1="27.7" x2="896.4" y2="-281.4" />
              <line x1="1188.6" y1="-120" x2="879.5" y2="189" />
              <line x1="-247.4" y1="1242.5" x2="61.6" y2="933.5" />
              <line x1="188.7" y1="806.4" x2="497.7" y2="497.4" />
              <line x1="789.9" y1="658.8" x2="480.8" y2="967.8" />
              <line x1="624.8" y1="370.4" x2="933.8" y2="61.3" />
              <line x1="1226" y1="222.7" x2="916.9" y2="531.7" />
              <line x1="1662.1" y1="-213.4" x2="1353" y2="95.6" />
            </g>
          </g>
        </svg>
      </View>
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        <View style={styles.container}>
          <View style={styles.buttonListContainer}>
            <ButtonList
              buttons={buttonsMenu({ navigation, equipo, nivelAcceso })}
              isMainMenu={true}
            />
          </View>
        </View>
      </ScrollView>

      {/* Franja amarilla de versión TEST */}
      {isTestVersion() && (
        <View style={styles.testVersionBanner}>
          <Text style={styles.testVersionText}>TEST VERSION</Text>
        </View>
      )}

      <Modal
        visible={settingsModalVisible}
        animationType='fade'
        transparent={true}
        onRequestClose={hideSettingsModal}>
        <TouchableWithoutFeedback onPress={hideSettingsModal}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback onPress={() => {}}>
              <View style={styles.settingsModalContainer}>
                <LinearGradient
                  colors={[ADMIN_SETTING_SCREEN_COLORS.BACKGROUND_WHITE, ADMIN_SETTING_SCREEN_COLORS.BACKGROUND_LIGHT]}
                  style={styles.settingsModalContent}>
                  <View style={styles.modalHeader}>
                    <MaterialCommunityIcons
                      name='cog'
                      size={24}
                      color={BRAND_COLORS.ACCENT}
                    />
                    <Text style={styles.modalTitle}>Configuración</Text>
                    <TouchableOpacity
                      onPress={hideSettingsModal}
                      style={styles.closeButton}>
                      <MaterialCommunityIcons
                        name='close'
                        size={20}
                        color={ADMIN_SETTING_SCREEN_COLORS.TEXT_MUTED}
                      />
                    </TouchableOpacity>
                  </View>

                  <View style={styles.modalBody}>
                    {settingsOptions.map((option, index) => (
                      <TouchableOpacity
                        key={option.screen}
                        style={[
                          styles.modalButton,
                          index === settingsOptions.length - 1 &&
                            styles.lastModalButton,
                        ]}
                        onPress={() => navigateToScreen(option.screen)}>
                        <MaterialCommunityIcons
                          name={option.icon}
                          size={20}
                          color={BRAND_COLORS.ACCENT}
                        />
                        <Text style={styles.modalButtonText}>
                          {option.title}
                        </Text>
                        <MaterialCommunityIcons
                          name='chevron-right'
                          size={20}
                          color={ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY}
                        />
                      </TouchableOpacity>
                    ))}
                  </View>
                </LinearGradient>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Botones de alertas en la parte inferior */}
      {shouldShowAlert && (
        <View style={styles.alertButtonsContainer}>
          {/* Botón de referencias sin imágenes */}
          <TouchableOpacity 
            style={styles.alertIconButton} 
            onPress={showModalReferencias}
            disabled={modalReferenciasVisible}
            onMouseEnter={(e) => {
              if (Platform.OS === 'web' && !modalReferenciasVisible) {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.transition = 'transform 0.2s';
              }
            }}
            onMouseLeave={(e) => {
              if (Platform.OS === 'web') {
                e.currentTarget.style.transform = 'scale(1)';
              }
            }}>
            <View
              style={[
                styles.alertIconSimple,
                {
                  backgroundColor:
                    referenciasLoaded && referenciasSinImagen.length > 0 
                      ? BRAND_COLORS.ACCENT
                      : ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY,
                  opacity: modalReferenciasVisible ? 0.7 : 1,
                },
              ]}>
              <MaterialCommunityIcons
                name='image-off'
                size={20}
                color='white'
              />
            </View>
          </TouchableOpacity>

          {/* Botón de alertas de piedras */}
          <TouchableOpacity 
            style={styles.alertIconButton} 
            onPress={showModal}
            disabled={modalVisible}
            onMouseEnter={(e) => {
              if (Platform.OS === 'web' && !modalVisible) {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.transition = 'transform 0.2s';
              }
            }}
            onMouseLeave={(e) => {
              if (Platform.OS === 'web') {
                e.currentTarget.style.transform = 'scale(1)';
              }
            }}>
            <View
              style={[
                styles.alertIconSimple,
                {
                  backgroundColor:
                    messages.length > 0
                      ? BRAND_COLORS.ACCENT
                      : ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY,
                  opacity: modalVisible ? 0.7 : 1,
                },
              ]}>
              <MaterialCommunityIcons
                name='bell-alert'
                size={20}
                color='white'
              />
            </View>
          </TouchableOpacity>

          {/* Botón de alertas de procesos */}
          <TouchableOpacity 
            style={styles.alertIconButton} 
            onPress={() => setModalProcessAlertsVisible(true)}
            disabled={modalProcessAlertsVisible}
            onMouseEnter={(e) => {
              if (Platform.OS === 'web' && !modalProcessAlertsVisible) {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.transition = 'transform 0.2s';
              }
            }}
            onMouseLeave={(e) => {
              if (Platform.OS === 'web') {
                e.currentTarget.style.transform = 'scale(1)';
              }
            }}>
            <View
              style={[
                styles.alertIconSimple,
                {
                  backgroundColor: BRAND_COLORS.ACCENT,
                  opacity: modalProcessAlertsVisible ? 0.7 : 1,
                },
              ]}>
              <MaterialCommunityIcons
                name='clock-alert'
                size={20}
                color='white'
              />
            </View>
          </TouchableOpacity>
        </View>
      )}

      {/* Modal de referencias sin imágenes */}
      {shouldShowAlert && modalReferenciasVisible && (
        <Modal
          visible={modalReferenciasVisible}
          animationType='none'
          transparent={true}
          onRequestClose={hideModalReferencias}>
          <TouchableWithoutFeedback onPress={hideModalReferencias}>
            <Animated.View style={[styles.alertOverlay, { opacity: fadeAnimReferencias }]}>
              <TouchableWithoutFeedback onPress={() => {}}>
                <Animated.View
                  style={[
                    styles.alertModalContent,
                    {
                      transform: [{ scale: scaleAnimReferencias }],
                      width: isExpandedReferencias ? "95%" : "90%",
                      maxHeight: isExpandedReferencias ? "80%" : "60%",
                    },
                  ]}>
                  <LinearGradient
                    colors={[ADMIN_SETTING_SCREEN_COLORS.BACKGROUND_WHITE, ADMIN_SETTING_SCREEN_COLORS.BACKGROUND_LIGHT]}
                    style={styles.alertGradientContainer}>
                    <View style={styles.alertHeader}>
                      <View style={styles.alertTitleContainer}>
                        <MaterialCommunityIcons
                          name='image-off'
                          size={20}
                          color={
                            referenciasSinImagen.length > 0
                              ? STATUS_COLORS.ERROR
                              : BACKGROUND_COLORS.BLUISH_GRAY
                          }
                        />
                        <View style={styles.alertTitleTextContainer}>
                          <Text style={styles.alertTitle}>
                            Referencias Sin Imágenes
                          </Text>
                          <Text style={styles.alertSubtitle}>
                            {referenciasSinImagen.length === 0
                              ? "No hay referencias sin imágenes"
                              : searchTermReferencias.trim()
                              ? `${filteredReferenciasSinImagen.length} de ${referenciasSinImagen.length} referencia${
                                  referenciasSinImagen.length !== 1 ? "s" : ""
                                }`
                              : `${referenciasSinImagen.length} referencia${
                                  referenciasSinImagen.length !== 1 ? "s" : ""
                                } sin imagen${referenciasSinImagen.length !== 1 ? "es" : ""}`}
                          </Text>
                        </View>
                      </View>

                      <View style={styles.alertActions}>
                        {referenciasSinImagen.length > 0 && (
                          <TouchableOpacity
                            onPress={handleExportExcelReferencias}
                            style={styles.actionButton}>
                            <MaterialCommunityIcons
                              name='download'
                              size={18}
                              color={BRAND_COLORS.ACCENT}
                            />
                          </TouchableOpacity>
                        )}
                        <TouchableOpacity
                          onPress={toggleExpandedReferencias}
                          style={styles.actionButton}>
                          <MaterialCommunityIcons
                            name={
                              isExpandedReferencias ? "arrow-collapse" : "arrow-expand"
                            }
                            size={18}
                            color={BRAND_COLORS.ACCENT}
                          />
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={hideModalReferencias}
                          style={styles.actionButton}>
                          <MaterialCommunityIcons
                            name='close'
                            size={18}
                            color={TEXT_COLORS.SECONDARY}
                          />
                        </TouchableOpacity>
                      </View>
                    </View>

                    {/* Buscador */}
                    <View style={styles.searchContainer}>
                      <MaterialCommunityIcons
                        name='magnify'
                        size={20}
                        color={ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY}
                        style={styles.searchIcon}
                      />
                      <TextInput
                        style={styles.searchInput}
                        placeholder="Buscar por referencia, descripción..."
                        placeholderTextColor={ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY}
                        value={searchTermReferencias}
                        onChangeText={setSearchTermReferencias}
                      />
                      {searchTermReferencias.length > 0 && (
                        <TouchableOpacity
                          onPress={() => setSearchTermReferencias("")}
                          style={styles.clearButton}>
                          <MaterialCommunityIcons
                            name='close-circle'
                            size={20}
                            color={ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY}
                          />
                        </TouchableOpacity>
                      )}
                    </View>

                    <View style={styles.alertContent}>
                      {isLoadingReferencias ? (
                        <View style={styles.loadingContainer}>
                          <Text style={styles.loadingText}>Cargando referencias...</Text>
                        </View>
                      ) : (
                        <FlatList
                          data={
                            filteredReferenciasSinImagen.length > 0
                              ? filteredReferenciasSinImagen
                              : [
                                  {
                                    referencia: "0",
                                    descripcion: searchTermReferencias.trim() 
                                      ? "No se encontraron resultados" 
                                      : "No hay referencias sin imágenes",
                                    fullRefs: [],
                                  },
                                ]
                          }
                          keyExtractor={(item) => item.referencia}
                          renderItem={({ item }) => <ReferenciaItem item={item} />}
                          showsVerticalScrollIndicator={false}
                          contentContainerStyle={styles.alertListContainer}
                        />
                      )}
                    </View>
                  </LinearGradient>
                </Animated.View>
              </TouchableWithoutFeedback>
            </Animated.View>
          </TouchableWithoutFeedback>
        </Modal>
      )}

      {shouldShowAlert && modalVisible && (
        <Modal
          visible={modalVisible}
          animationType='none'
          transparent={true}
          onRequestClose={hideModal}>
          <TouchableWithoutFeedback onPress={hideModal}>
            <Animated.View style={[styles.alertOverlay, { opacity: fadeAnim }]}>
              <TouchableWithoutFeedback onPress={() => {}}>
                <Animated.View
                  style={[
                    styles.alertModalContent,
                    {
                      transform: [{ scale: scaleAnim }],
                      width: isExpanded ? "95%" : "90%",
                      maxHeight: isExpanded ? "80%" : "60%",
                    },
                  ]}>
                  <LinearGradient
                    colors={[ADMIN_SETTING_SCREEN_COLORS.BACKGROUND_WHITE, ADMIN_SETTING_SCREEN_COLORS.BACKGROUND_LIGHT]}
                    style={styles.alertGradientContainer}>
                    <View style={styles.alertHeader}>
                      <View style={styles.alertTitleContainer}>
                        <MaterialCommunityIcons
                          name='alert-circle'
                          size={20}
                          color={
                            messages.length > 0
                              ? STATUS_COLORS.ERROR
                              : BACKGROUND_COLORS.BLUISH_GRAY
                          }
                        />
                        <View style={styles.alertTitleTextContainer}>
                          <Text style={styles.alertTitle}>
                            Control de Piedras
                          </Text>
                          <Text style={styles.alertSubtitle}>
                            {messages.length === 0
                              ? "No hay alertas pendientes"
                              : searchTermPiedras.trim()
                              ? `${filteredMessages.length} de ${messages.length} alerta${
                                  messages.length !== 1 ? "s" : ""
                                }`
                              : `${messages.length} alerta${
                                  messages.length !== 1 ? "s" : ""
                                } pendiente${messages.length !== 1 ? "s" : ""}`}
                          </Text>
                        </View>
                      </View>

                      <View style={styles.alertActions}>
                        {messages.length > 0 && (
                          <TouchableOpacity
                            onPress={handleExportExcel}
                            style={styles.actionButton}>
                            <MaterialCommunityIcons
                              name='download'
                              size={18}
                              color={BRAND_COLORS.ACCENT}
                            />
                          </TouchableOpacity>
                        )}
                        <TouchableOpacity
                          onPress={toggleExpanded}
                          style={styles.actionButton}>
                          <MaterialCommunityIcons
                            name={
                              isExpanded ? "arrow-collapse" : "arrow-expand"
                            }
                            size={18}
                            color={BRAND_COLORS.ACCENT}
                          />
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={hideModal}
                          style={styles.actionButton}>
                          <MaterialCommunityIcons
                            name='close'
                            size={18}
                            color={TEXT_COLORS.SECONDARY}
                          />
                        </TouchableOpacity>
                      </View>
                    </View>

                    {/* Buscador */}
                    <View style={styles.searchContainer}>
                      <MaterialCommunityIcons
                        name='magnify'
                        size={20}
                        color={ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY}
                        style={styles.searchIcon}
                      />
                      <TextInput
                        style={styles.searchInput}
                        placeholder="Buscar por MO, mensaje, días..."
                        placeholderTextColor={ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY}
                        value={searchTermPiedras}
                        onChangeText={setSearchTermPiedras}
                      />
                      {searchTermPiedras.length > 0 && (
                        <TouchableOpacity
                          onPress={() => setSearchTermPiedras("")}
                          style={styles.clearButton}>
                          <MaterialCommunityIcons
                            name='close-circle'
                            size={20}
                            color={ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY}
                          />
                        </TouchableOpacity>
                      )}
                    </View>

                    <View style={styles.alertContent}>
                      <FlatList
                        data={
                          filteredMessages.length > 0
                            ? filteredMessages
                            : [
                                {
                                  mo: "0",
                                  message: searchTermPiedras.trim() 
                                    ? "No se encontraron resultados" 
                                    : "No hay ninguna MO pendiente",
                                  priority: "none",
                                },
                              ]
                        }
                        keyExtractor={(item) => item.mo}
                        renderItem={({ item }) => <AlertItem item={item} />}
                        showsVerticalScrollIndicator={false}
                        contentContainerStyle={styles.alertListContainer}
                      />
                    </View>
                  </LinearGradient>
                </Animated.View>
              </TouchableWithoutFeedback>
            </Animated.View>
          </TouchableWithoutFeedback>
        </Modal>
      )}

      {/* Modal de alertas de procesos */}
      {shouldShowAlert && (
        <ComponentProcessAlertsModal
          visible={modalProcessAlertsVisible}
          onClose={() => setModalProcessAlertsVisible(false)}
        />
      )}
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  background: {
    flex: 1,
    position: "relative",
  },
  svgBackgroundContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    height: "100%",
    zIndex: 0,
    overflow: "hidden",
  },
  svgBackground: {
    position: "absolute",
    width: "100%",
    height: "100%",
    opacity: 0.1,
    ...(Platform.OS === "web" && {
      // Estilos específicos para web
      pointerEvents: "none",
    }),
  },
  scrollContainer: {
    flexGrow: 1,
    alignItems: "center",
    width: "100%",
    paddingBottom: 20,
    minHeight: "100%",
  },
  container: {
    flex: 1,
    alignItems: "center",
    width: "100%",
    paddingTop: 12,
    justifyContent: "flex-start",
  },
  headerContainer: {
    width: "100%",
    paddingHorizontal: 20,
    paddingVertical: 12,
    marginBottom: 8,
  },
  headerInfoContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 16,
  },
  userBadge: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 16,
    gap: 8,
    shadowColor: ADMIN_SETTING_SCREEN_COLORS.SHADOW_DARK,
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 4,
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.3)",
  },
  versionBadge: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 16,
    gap: 8,
    shadowColor: ADMIN_SETTING_SCREEN_COLORS.SHADOW_DARK,
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 4,
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.3)",
  },
  contadorVersion: {
    fontFamily: "monospace",
    color: ADMIN_SETTING_SCREEN_COLORS.TEXT_MUTED,
    fontSize: 13,
    fontWeight: "600",
    letterSpacing: 0.3,
  },
  buttonListContainer: {
    flex: 1,
    width: "100%",
  },
  headerRightContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  headerUserInfo: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerUserBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  headerVersionBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  headerUserText: {
    color: TEXT_COLORS.LIGHT,
    fontSize: 14,
    fontWeight: "500",
  },
  headerVersionText: {
    color: TEXT_COLORS.LIGHT,
    fontSize: 14,
    fontWeight: "500",
  },
  headerButton: {
    marginHorizontal: 8,
    padding: 8,
  },

  // Modal de configuración
  modalOverlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(23, 64, 69, 0.55)",
  },
  settingsModalContainer: {
    margin: 20,
    width: "92%",
    maxWidth: 520,
  },
  settingsModalContent: {
    borderRadius: 16,
    shadowColor: SHADOW_COLORS.PRIMARY,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 8,
    borderWidth: 1,
    borderColor: BORDER_COLORS.PRIMARY,
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_SETTING_SCREEN_COLORS.BORDER_LIGHT,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "700",
    color: ADMIN_SETTING_SCREEN_COLORS.TEXT_PRIMARY,
    flex: 1,
    marginLeft: 12,
  },
  closeButton: {
    padding: 4,
  },
  modalBody: {
    padding: 8,
  },
  modalButton: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_SETTING_SCREEN_COLORS.BORDER_SECONDARY,
  },
  lastModalButton: {
    borderBottomWidth: 0,
  },
  modalButtonText: {
    color: ADMIN_SETTING_SCREEN_COLORS.TEXT_DARK,
    fontSize: 16,
    fontWeight: "500",
    flex: 1,
    marginLeft: 12,
  },

  // Contenedor de botones de alertas
  alertButtonsContainer: {
    position: "absolute",
    bottom: 16,
    right: 16,
    flexDirection: "row",
    gap: 12,
    zIndex: 1000,
  },
  alertIconButton: {
    borderRadius: 18,
    shadowColor: SHADOW_COLORS.PRIMARY,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 4,
  },
  alertIconSimple: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: "center",
    alignItems: "center",
  },

  // Modal de alertas
  alertOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(23, 64, 69, 0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
  },
  alertModalContent: {
    backgroundColor: ADMIN_SETTING_SCREEN_COLORS.BACKGROUND_CARD,
    borderRadius: 16,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: `0 8px 16px ${SHADOW_COLORS.PRIMARY}33`,
    borderWidth: 1,
    borderColor: BORDER_COLORS.PRIMARY,
    width: "92%",
    maxWidth: 980,
  },
  alertGradientContainer: {
    borderRadius: 16,
    overflow: "hidden",
    flex: 1,
  },
  alertHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: BACKGROUND_COLORS.BLUISH_GRAY,
  },
  alertTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  alertTitleTextContainer: {
    marginLeft: 10,
    flex: 1,
  },
  alertTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: BRAND_COLORS.ACCENT,
  },
  alertSubtitle: {
    fontSize: 12,
    color: TEXT_COLORS.SECONDARY,
    marginTop: 2,
  },
  alertActions: {
    flexDirection: "row",
    alignItems: "center",
  },
  actionButton: {
    padding: 6,
    marginLeft: 2,
    borderRadius: 6,
  },
  alertContent: {
    flex: 1,
    padding: 12,
  },
  alertListContainer: {
    paddingBottom: 8,
  },
  alertItem: {
    backgroundColor: BACKGROUND_COLORS.CARD,
    marginBottom: 8,
    padding: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: BACKGROUND_COLORS.BLUISH_GRAY,
    shadowColor: SHADOW_COLORS.PRIMARY,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.03,
    shadowRadius: 2,
    elevation: 1,
  },
  alertItemHigh: {
    borderColor: STATUS_COLORS.ERROR,
    backgroundColor: ADMIN_SETTING_SCREEN_COLORS.BACKGROUND_ERROR,
  },
  alertItemHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 6,
  },
  priorityIndicator: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginRight: 6,
  },
  alertMoText: {
    fontSize: 13,
    fontWeight: "600",
    color: BRAND_COLORS.ACCENT,
    fontFamily: "monospace",
    flex: 1,
  },
  daysChip: {
    backgroundColor: STATUS_COLORS.ERROR,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
  },
  daysText: {
    color: TEXT_COLORS.LIGHT,
    fontSize: 10,
    fontWeight: "600",
  },
  alertText: {
    fontSize: 12,
    color: ADMIN_SETTING_SCREEN_COLORS.TEXT_DARK,
    lineHeight: 16,
    fontFamily: "monospace",
  },
  loadingContainer: {
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  loadingText: {
    fontSize: 14,
    color: ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: BACKGROUND_COLORS.SECONDARY,
    borderRadius: 8,
    marginHorizontal: 16,
    marginVertical: 12,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: ADMIN_SETTING_SCREEN_COLORS.BORDER_SECONDARY,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: ADMIN_SETTING_SCREEN_COLORS.TEXT_DARK,
    padding: 0,
  },
  clearButton: {
    marginLeft: 8,
    padding: 4,
  },
  
  // Franja de versión TEST
  testVersionBanner: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 85,
    backgroundColor: "rgba(185, 34, 34, 0.9)",
    paddingVertical: 12,
    paddingHorizontal: 20,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: SHADOW_COLORS.PRIMARY,
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
    zIndex: 1000,
  },
  testVersionText: {
    fontSize: 18,
    fontWeight: "700",
    color: ADMIN_SETTING_SCREEN_COLORS.TEXT_LIGHT_GRAY,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
});
